/**
 * Optimized Banner Service with React Query caching and memory optimization
 */
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import axiosInstance from "../components/auth/interceptor";
import { ReadBanner } from "../interfaces/core/ReadBanner";

const baseURL = process.env.REACT_APP_API_URL;

// Cache configuration
const BANNER_CACHE_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds
const BANNER_STALE_TIME = 2 * 60 * 1000; // 2 minutes in milliseconds

/**
 * Fetch banners from API with optimized caching
 */
async function fetchBannersFromAPI(): Promise<ReadBanner[]> {
  const response = await axiosInstance.get(`${baseURL}/banners`, {
    cache: {
      ttl: BANNER_CACHE_TIME,
    },
    headers: {
      'Cache-Control': 'public, max-age=300', // 5 minutes client cache
    }
  });
  return response.data as ReadBanner[];
}

/**
 * Custom hook to get banners with React Query optimization
 */
export const useBanners = (): UseQueryResult<ReadBanner[], Error> => {
  return useQuery({
    queryKey: ['banners'],
    queryFn: fetchBannersFromAPI,
    staleTime: BANNER_STALE_TIME,
    gcTime: BANNER_CACHE_TIME, // Updated from cacheTime to gcTime for React Query v5
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: (failureCount, error: any) => {
      // Don't retry on 404 errors
      if (error?.status === 404 || error?.name === 'NotFoundError') {
        return false;
      }
      return failureCount < 2; // Reduced retry attempts for memory efficiency
    }
  });
};

/**
 * Legacy function for backward compatibility (now uses React Query under the hood)
 */
export async function getBanners(): Promise<ReadBanner[]> {
  return fetchBannersFromAPI();
}

/**
 * Preload critical banners (first 2) for performance
 */
export const preloadCriticalBanners = (banners: ReadBanner[]) => {
  if (!banners || banners.length === 0) return;
  
  // Preload only the first 2 banners
  const criticalBanners = banners.slice(0, 2);
  
  criticalBanners.forEach((banner, index) => {
    if (banner.image) {
      const img = new Image();
      img.loading = 'eager'; // High priority loading
      img.fetchPriority = 'high'; // High priority for critical images
      img.src = banner.image.startsWith('http') ? banner.image : `${baseURL}/${banner.image}`;
      
      // Optional: add to cache for faster access
      img.onload = () => {
        console.log(`Critical banner ${index + 1} preloaded successfully`);
      };
      
      img.onerror = () => {
        console.warn(`Failed to preload critical banner ${index + 1}`);
      };
    }
  });
};
